<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { useBookingsStore } from "~/store/bookings/bookings.store";
import { useEmployeeTrainingsStore } from "~/store/employee-trainings/employee-trainings.store";
import { RolesEnums } from "~/utils/enums/roles.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import { usePagination } from "~/composables/paginations/usePagination";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import AppButton from "~/components/Common/Buttons/AppButton.vue";

const sessionsStore = useSessionsStore();
const authStore = useAuthStore();

const bookingStore = useBookingsStore();
const employeeTrainingsStore = useEmployeeTrainingsStore();
const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
const sessionDetailsStore = useSessionDetailsStore();
const { trainingPlanSessionIndividual, showUpdateSessionModal, showSessionStatusActionModal, sessions } =
  storeToRefs(sessionsStore);

const useAuth = useAuthStore();

const { user } = storeToRefs(useAuth);

defineProps({
  disable: {
    type: Boolean,
    default: false,
  },
});

const isMyTrainingsConfirmation: boolean =
  !showSessionStatusActionModal.value.registered && !showUpdateSessionModal.value;

const emit = defineEmits(["showDoneModal", "loaded", "hide"]);

function hide() {
  emit("hide");
}

async function confirm() {
  sessionsStore.errorConfirmProject = false;

  const session = sessions.value?.find(
    (session) => session.sessionId === trainingPlanSessionIndividual.value?.sessionId,
  );
  if (
    (authStore.isEmployee || authStore.isOnlyManager) &&
    ((session && session.nbAttendees && session?.maxNbAttendees && session.nbAttendees > session?.maxNbAttendees) ||
      (trainingPlanSessionIndividual.value?.nbAttendees &&
        trainingPlanSessionIndividual.value?.maxNbAttendees &&
        trainingPlanSessionIndividual.value?.nbAttendees >
          trainingPlanSessionIndividual.value?.maxNbAttendees)) &&
    session?.overbookingNotAllowed
  ) {
    sessionsStore.errorConfirmProject = true;
  } else if (trainingPlanSessionIndividual.value?.bookingId && trainingPlanSessionIndividual.value?.sessionId) {
    if (
      user.value?.roles.find(
        (u) => u.name === (RolesEnums.COMPANY_ADMIN || RolesEnums.GROUP_ADMIN || RolesEnums.TRAINING_ADMIN),
      ) &&
      trainingPlanSessionIndividual.value?.userId !== user?.value.userId
    ) {
      await bookingStore.updateBookingNotificationsSettings(trainingPlanSessionIndividual.value?.bookingId, {
        bookingConfirmed: false,
      });
    }

    const confirmed = await bookingStore.confirmBooking(
      trainingPlanSessionIndividual.value.bookingId,
      trainingPlanSessionIndividual.value.sessionId,
    );
    if (!confirmed) {
      return;
    }
    emit("showDoneModal");

    if (!isMyTrainingsConfirmation) {
      await refreshDatas();
    } else {
      await employeeTrainingsStore.fetchAllEmployeeTrainings();
      await employeeTrainingsStore.fetchEmployeeTraining(trainingPlanSessionIndividual.value.bookingId);
      await sessionsStore.fetchTrainingPlanSession(trainingPlanSessionIndividual.value.id);
      employeeTrainingsStore.showEmployeeBookingDetails = false;

      const paginate = usePagination();

      await paginate.fetchData();
    }
  }
}

async function updateBookingSession() {
  if (trainingPlanSessionIndividual.value?.sessionId && trainingPlanSessionIndividual.value?.bookingId) {
    await bookingStore.updateBookingSession({
      booking_id: trainingPlanSessionIndividual.value?.bookingId,
      new_session_id: trainingPlanSessionIndividual.value?.sessionId,
    });
    // showUpdateSessionModal.value = false;

    emit("showDoneModal");
    await refreshDatas();
  }
}

async function refreshDatas() {
  if (trainingPlanSessionIndividual.value?.id) {
    const $router = useRouter();
    const routeName: string = $router.currentRoute.value.name as string;
    await sessionsStore.fetchTrainingPlanSession(trainingPlanSessionIndividual.value.id);

    // /registrations is the only list that keys its side-modal off ?details_id. my-trainings and
    // learning-path host the same side-modal on their own URL, so replacing it ejects the user.
    const isRegistrationsList =
      !sessionDetailsStore.sessionViewModal &&
      !routeName.includes("my-trainings") &&
      !routeName.includes("learning-path");

    if (isRegistrationsList) {
      $router.replace(`/registrations?details_id=${trainingPlanSessionIndividual.value.id}&type=session`);
    } else if (sessionDetailsStore.sessionViewModal) {
      await sessionDetailsStore.fetchSessionDetails(
        sessionDetailsStore.sessionDetails?.sessionId!,
        sessionDetailsStore.sessionDetails?.itemType!,
      );
      await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(
        sessionDetailsStore.sessionDetails?.sessionId!,
        sessionDetailsStore.sessionDetails?.itemType!,
      );
    } else if (routeName.includes("my-trainings")) {
      await sessionsStore.fetchTrainingPlanSession(trainingPlanSessionIndividual.value.id);
    }
  }
}

function noSession() {
  if (!trainingPlanSessionIndividual.value?.startDate && !trainingPlanSessionIndividual.value?.sessionId) {
    return true;
  }
  return false;
}
</script>

<template>
  <footer v-if="!showUpdateSessionModal && !disable" class="invite_step_date__footer_container">
    <AppButton type="outlined" label="Annuler" class="w-full" @click="hide" />
    <AppButton label="Confirmer" :disabled="noSession()" class="w-full" @click="confirm" />
  </footer>

  <footer v-else-if="showUpdateSessionModal" class="invite_step_date__footer_container">
    <AppButton type="outlined" label="Annuler" class="w-full" @click="hide" />
    <AppButton label="Valider" :disabled="noSession()" class="w-full" @click="updateBookingSession()" />
  </footer>
</template>

<style scoped lang="scss">
.invite_step_date__footer_container {
  @apply flex items-center px-[64px] w-full flex-row justify-between gap-[16px];
  height: 68px;
  min-height: 68px;
  border-top: 1px solid var(--grey-lightest);
}

@media (max-width: 768px) {
  .invite_step_date__footer_container {
    @apply flex flex-col gap-[8px] px-[24px] py-[16px];
    min-height: 0px;
    height: auto;
  }
}
</style>
